'use client';

import React, { useState } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import type { AppraisalCycle } from '@/store/appraisalStore';

interface Props {
  cycle: AppraisalCycle;
  onClose: () => void;
  onConfirm: () => void;
}

export default function CycleDeleteModal({ cycle, onClose, onConfirm }: Props) {
  const [confirmText, setConfirmText] = useState('');
  const isConfirmed = confirmText === cycle.name;
  const isDraftOrArchived = ['draft', 'archived'].includes(cycle.status);

  return (
    <div className="pn-appraisal__modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
      <div className="pn-appraisal__modal pn-appraisal__modal--sm">
        <div className="pn-appraisal__modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 'var(--pn-radius)', background: 'var(--pn-danger-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={18} color="var(--pn-danger)" />
            </div>
            <div>
              <div className="pn-appraisal__modal-header-title" id="delete-modal-title">Delete Appraisal Cycle</div>
              <div className="pn-appraisal__modal-header-subtitle">This action cannot be undone</div>
            </div>
          </div>
          <button className="pn-appraisal__modal-header-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="pn-appraisal__modal-body">
          {!isDraftOrArchived && (
            <div style={{ background: 'var(--pn-warning-light)', border: '1px solid var(--pn-warning-mid)', borderRadius: 'var(--pn-radius)', padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--pn-warning)', display: 'flex', gap: 8 }}>
              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>This cycle has submitted appraisals. Deleting it will permanently remove all associated employee data, manager reviews, and audit records.</span>
            </div>
          )}

          <p style={{ fontSize: 13.5, color: 'var(--pn-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
            You are about to permanently delete <strong style={{ color: 'var(--pn-text-primary)' }}>"{cycle.name}"</strong>.
            {cycle.totalAssigned > 0 && ` This will affect ${cycle.totalAssigned} assigned employees and ${cycle.submitted} submitted appraisals.`}
          </p>

          <div className="pn-appraisal__form-field">
            <label className="pn-appraisal__form-label" htmlFor="delete-confirm">
              Type the cycle name to confirm deletion:
            </label>
            <p className="pn-appraisal__form-hint" style={{ marginBottom: 6 }}>
              <code style={{ background: 'var(--pn-surface-2)', padding: '1px 6px', borderRadius: 4, fontSize: 12, color: 'var(--pn-text-primary)' }}>{cycle.name}</code>
            </p>
            <input
              id="delete-confirm"
              className="pn-appraisal__form-input"
              placeholder="Type cycle name exactly…"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="pn-appraisal__modal-footer">
          <button type="button" className="pn-appraisal__btn pn-appraisal__btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="pn-appraisal__btn pn-appraisal__btn--danger"
            onClick={onConfirm}
            disabled={!isConfirmed}
          >
            <Trash2 size={14} /> Delete Cycle
          </button>
        </div>
      </div>
    </div>
  );
}